/**
 * AIReviewModal — modal del flow de revisión IA para campañas custom.
 *
 * Tres modos según el status de la campaña:
 *
 *   pending_review → loader animado 6s + auto-trigger onRunReview()
 *                    Cuando vuelve el resultado, la campaña ya tiene
 *                    status === 'active' o === 'needs_changes' y el
 *                    modal renderiza el modo correspondiente.
 *
 *   active         → estado de éxito (verdict 'approved'): mensaje firmado
 *                    por la IA + CTA "Listo".
 *
 *   needs_changes  → lista de observaciones por categoría con sugerencias
 *                    accionables + textarea para editar el copy + CTA
 *                    "Reenviar a revisión".
 */

import { useEffect, useState } from "react";
import {
  X,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  Info,
  Loader2,
  Send,
  RefreshCw,
  Volume2,
  Clock,
  Ban,
  Mic,
  Wand2,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import type {
  AIObservationCategory,
  Objective,
  AIObservation,
} from "../types";

interface AIReviewModalProps {
  campaign: Objective;
  onClose: () => void;
  /** Trigger del review (handler que llama el endpoint /ai-review) */
  onRunReview: () => void;
  /** Guarda cambios y reenvía a revisión */
  onResubmit: (payload: { name?: string; messagePreview?: string }) => void;
}

/* ─── Mapeo category → icon ─── */
const CATEGORY_ICON: Record<AIObservationCategory, React.ReactNode> = {
  tone_mismatch: <Mic size={14} />,
  frequency_conflict: <Clock size={14} />,
  forbidden_word: <Ban size={14} />,
  campaign_clash: <Volume2 size={14} />,
  spam_risk: <AlertOctagon size={14} />,
  timing_issue: <Clock size={14} />,
  copy_improvement: <Wand2 size={14} />,
};

const CATEGORY_LABEL: Record<AIObservationCategory, string> = {
  tone_mismatch: "Tono fuera de manual",
  frequency_conflict: "Conflicto de frecuencia",
  forbidden_word: "Palabras prohibidas",
  campaign_clash: "Choque con otra campaña",
  spam_risk: "Riesgo de spam",
  timing_issue: "Problema de horario",
  copy_improvement: "Mejora de copy",
};

const SEVERITY_META: Record<
  AIObservation["severity"],
  { bg: string; border: string; text: string; icon: React.ReactNode }
> = {
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    icon: <Info size={11} />,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    icon: <AlertTriangle size={11} />,
  },
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    icon: <AlertOctagon size={11} />,
  },
};

export default function AIReviewModal({
  campaign,
  onClose,
  onRunReview,
  onResubmit,
}: AIReviewModalProps) {
  const isPending = campaign.status === "pending_review";
  const isApproved = campaign.aiReview?.verdict === "approved";
  const needsChanges = campaign.status === "needs_changes";

  const [editedMessage, setEditedMessage] = useState(
    campaign.messagePreview || "",
  );
  const [submitting, setSubmitting] = useState(false);

  /* ESC + scroll lock */
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onClose();
    };
    window.addEventListener("keydown", handleEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose, isPending]);

  /**
   * Auto-trigger del review tras 4s cuando está pending.
   *
   * Importante: el guard se basa en (status + iteration), no en un flag
   * mutable. Esto permite que después de "Reenviar a revisión" (que
   * cambia status pending → active/needs_changes y luego status →
   * pending de nuevo con iteration++), el efecto vuelva a dispararse.
   */
  const reviewKey = `${campaign.id}-${campaign.aiReview?.iteration ?? 0}-${campaign.status}`;
  useEffect(() => {
    if (!isPending) return;
    const t = setTimeout(() => {
      onRunReview();
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewKey, isPending]);

  const handleResubmit = () => {
    setSubmitting(true);
    onResubmit({ messagePreview: editedMessage });
    // El padre setea active el modal nuevamente con la nueva data
    // y el isPending vuelve a ser true → loader otra vez.
    setSubmitting(false);
  };

  /* ───────── Render ───────── */

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 animate-fade-in"
        onClick={isPending ? undefined : onClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto animate-zoom-in"
          role="dialog"
          aria-modal="true"
        >
          {/* HEADER que cambia según verdict */}
          {isPending ? (
            <PendingHeader campaignName={campaign.name} />
          ) : isApproved ? (
            <ApprovedHeader campaignName={campaign.name} onClose={onClose} />
          ) : (
            <NeedsChangesHeader
              campaignName={campaign.name}
              iteration={campaign.aiReview?.iteration || 1}
              onClose={onClose}
            />
          )}

          {/* BODY que cambia según modo */}
          <div className="flex-1 overflow-y-auto">
            {isPending && <PendingBody />}
            {isApproved && (
              <ApprovedBody
                approverNote={campaign.aiReview?.approverNote}
                campaign={campaign}
              />
            )}
            {needsChanges && (
              <NeedsChangesBody
                observations={campaign.aiReview?.observations || []}
                editedMessage={editedMessage}
                onEditMessage={setEditedMessage}
              />
            )}
          </div>

          {/* FOOTER */}
          {!isPending && (
            <div className="border-t border-slate-100 p-4 flex items-center justify-end gap-2 bg-slate-50/50">
              {isApproved ? (
                <Button variant="primary" onClick={onClose}>
                  <CheckCircle2 size={14} />
                  Listo, ver mis campañas
                </Button>
              ) : (
                <>
                  <Button variant="secondary" onClick={onClose} disabled={submitting}>
                    Volver más tarde
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleResubmit}
                    disabled={submitting || !editedMessage.trim()}
                  >
                    {submitting ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Reenviar a revisión
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Sub-componentes: 3 headers ─── */

function PendingHeader({ campaignName }: { campaignName: string }) {
  return (
    <div className="px-6 py-5 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white relative overflow-hidden">
      {/* Animated pulse rings */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="relative flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
          <Loader2 size={20} className="animate-spin" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-90 mb-0.5">
            Revisando…
          </div>
          <h2 className="text-xl font-bold leading-tight truncate">
            {campaignName}
          </h2>
        </div>
      </div>
    </div>
  );
}

function ApprovedHeader({
  campaignName,
  onClose,
}: {
  campaignName: string;
  onClose: () => void;
}) {
  return (
    <div className="px-6 py-5 bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 text-white relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <CheckCircle2 size={22} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-90 mb-0.5 flex items-center gap-1">
              <Sparkles size={10} />
              Aprobada
            </div>
            <h2 className="text-xl font-bold leading-tight truncate">
              {campaignName}
            </h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

function NeedsChangesHeader({
  campaignName,
  iteration,
  onClose,
}: {
  campaignName: string;
  iteration: number;
  onClose: () => void;
}) {
  return (
    <div className="px-6 py-5 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={22} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-90 mb-0.5 flex items-center gap-1.5">
              Necesita ajustes
              <span className="bg-white/20 backdrop-blur-sm px-1.5 rounded-full">
                Revisión #{iteration}
              </span>
            </div>
            <h2 className="text-xl font-bold leading-tight truncate">
              {campaignName}
            </h2>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center transition-colors flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

/* ─── Bodies ─── */

function PendingBody() {
  return (
    <div className="p-8 flex flex-col items-center text-center">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center">
          <Sparkles size={28} className="text-violet-600 animate-pulse" />
        </div>
        <div className="absolute inset-0 rounded-full bg-violet-400/30 animate-ping" />
      </div>

      <h3 className="text-lg font-bold text-slate-900 mb-2">
        La IA está revisando tu campaña
      </h3>
      <p className="text-sm text-slate-600 max-w-md leading-relaxed mb-6">
        Tarda unos segundos. Estoy chequeando si encaja con tu manual de marca,
        si choca con otra campaña activa, si la frecuencia es razonable y si el
        copy puede disparar filtros de WhatsApp.
      </p>

      {/* Lista de chequeos animada (timing alineado con loader 4s) */}
      <div className="w-full max-w-sm space-y-2 text-left">
        <CheckRow label="Tono y voz vs tu manual" />
        <CheckRow label="Palabras prohibidas + spam risk" delay={800} />
        <CheckRow label="Conflicto con campañas activas" delay={1600} />
        <CheckRow label="Frecuencia y horario de envío" delay={2400} />
      </div>
    </div>
  );
}

function CheckRow({ label, delay = 0 }: { label: string; delay?: number }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), delay + 600);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div
      className={`flex items-center gap-2 text-xs ${
        done ? "text-emerald-700" : "text-slate-500"
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {done ? (
        <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
      ) : (
        <Loader2
          size={14}
          className="animate-spin text-slate-400 flex-shrink-0"
        />
      )}
      <span>{label}</span>
    </div>
  );
}

function ApprovedBody({
  approverNote,
  campaign,
}: {
  approverNote?: string;
  campaign: Objective;
}) {
  return (
    <div className="p-6 space-y-5">
      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 text-white flex items-center justify-center mb-4 shadow-lg shadow-emerald-200">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-2">
          Aprobada y lista para correr
        </h3>
        {approverNote && (
          <p className="text-sm text-slate-700 leading-relaxed max-w-md mx-auto">
            {approverNote}
          </p>
        )}
      </div>

      {campaign.messagePreview && (
        <section>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            Mensaje aprobado
          </h4>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
              {campaign.messagePreview}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function NeedsChangesBody({
  observations,
  editedMessage,
  onEditMessage,
}: {
  observations: AIObservation[];
  editedMessage: string;
  onEditMessage: (v: string) => void;
}) {
  return (
    <div className="p-6 space-y-5">
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
          Observaciones de la IA
        </h3>
        <div className="space-y-3">
          {observations.map((obs, i) => (
            <ObservationItem key={i} obs={obs} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
          Editá el mensaje y reenvialo
        </h3>
        <textarea
          value={editedMessage}
          onChange={(e) => onEditMessage(e.target.value)}
          rows={6}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 resize-none"
          placeholder="Tu mensaje aquí…"
        />
        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
          Al reenviar, la IA revisa de nuevo. Si todo OK, pasa a activa.
        </p>
      </section>
    </div>
  );
}

function ObservationItem({ obs }: { obs: AIObservation }) {
  const meta = SEVERITY_META[obs.severity];
  const icon = CATEGORY_ICON[obs.category];
  const label = CATEGORY_LABEL[obs.category];

  return (
    <div className={`border ${meta.border} ${meta.bg} rounded-xl p-4`}>
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 rounded-lg ${meta.text} bg-white border ${meta.border} flex items-center justify-center flex-shrink-0`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-xs font-bold ${meta.text}`}>{label}</span>
            <span
              className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.text} border ${meta.border} flex items-center gap-1`}
            >
              {meta.icon}
              {obs.severity}
            </span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            {obs.message}
          </p>
          {obs.suggestion && (
            <div className="bg-white/60 border border-white rounded-lg p-2.5 mt-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                <Wand2 size={9} />
                Sugerencia
              </p>
              <p className="text-xs text-slate-700 leading-relaxed">
                {obs.suggestion}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
